import { useMemo, useState } from 'react';
import type { Drn } from '@duke-hq/drn/type';
import type { ChatUser } from '@duke-hq/svc-support-web';
import { SupportContextNoop } from './ctx';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export function useChatActions({
  lookupUser,
  studentDrn,
  onClose,
}: {
  lookupUser?: (drn: Drn) => ChatUser | undefined;
  studentDrn?: Drn;
  onClose?: () => void;
}) {
  const [chatUserCache, setChatUserCache] = useState<Record<Drn, ChatUser>>({});

  const sendTyping = trpcAdmin.support.sendTyping.useMutation();
  const sendMessage = trpcAdmin.support.sendMessage.useMutation();
  const closeChat = trpcAdmin.support.chat.close.useMutation();
  const ensureChat = trpcAdmin.support.ensureChatForContextDrn.useMutation();

  const utils = trpcAdmin.useUtils();

  const chatActions: typeof SupportContextNoop = useMemo(
    () => ({
      archive: async (id) => {
        closeChat.mutate({ id });
        utils.support.chat.list.refetch();
        if (onClose) {
          onClose();
        }
      },
      actions: {
        sendTyping: async ({ chatId, typing }) => {
          sendTyping.mutate({ chatId, typing });
        },
        sendMessage: async ({ chatId, message }) => {
          sendMessage.mutate({ chatId, message });
        },
        getUser: (userDrn) => {
          if (chatUserCache && chatUserCache[userDrn]) {
            return chatUserCache[userDrn] as ChatUser | undefined;
          }
          // otherwise look in the original page cache
          if (typeof lookupUser === 'function') {
            const user = lookupUser(userDrn);
            if (user) {
              return user;
            }
          }
        },
        refetchChatMeta: async (chatId: string) => {
          utils.support.chat.getMeta.refetch({ id: chatId });
          utils.support.chat.list.refetch();
        },
        createStudentContextChat: async () => {
          if (!studentDrn) {
            return;
          }
          ensureChat.mutate({ contextDrn: studentDrn });
        },
      },
    }),
    [
      closeChat,
      utils.support.chat.list,
      utils.support.chat.getMeta,
      onClose,
      sendTyping,
      sendMessage,
      chatUserCache,
      lookupUser,
      ensureChat,
      studentDrn,
    ],
  );

  return {
    chatUserCache,
    setChatUserCache,
    chatActions,
  };
}
